import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface PDFExportOptions {
  widgetElement: HTMLElement;
  title: string;
  portfolioName?: string | null;
  headerRightText?: string;
}

export const exportWidgetToPDF = async ({
  widgetElement,
  title,
  portfolioName,
  headerRightText,
}: PDFExportOptions): Promise<void> => {
  // Find the card body which contains the chart
  const cardBody = widgetElement.querySelector('.p-card-body') as HTMLElement;

  if (!cardBody) {
    console.error('Chart container not found');
    return;
  }

  // Create PDF
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 5;
  let yPosition = margin;

  // Add header with logo
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.src = '/icon-512.png';

    await new Promise<void>((resolve) => {
      logoImg.onload = () => {
        try {
          const logoSize = 8;
          pdf.addImage(logoImg, 'PNG', margin, yPosition, logoSize, logoSize);
        } catch (error) {
          console.warn('Error adding logo:', error);
        }
        resolve();
      };
      logoImg.onerror = () => resolve();
      setTimeout(() => resolve(), 2000);
    });
  } catch (error) {
    console.warn('Error loading logo:', error);
  }

  // Add title
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const titleX = margin + 10;
  pdf.text(title, titleX, yPosition + 5);

  // Build header right text
  let finalHeaderRightText = headerRightText || '';
  if (portfolioName && !finalHeaderRightText) {
    finalHeaderRightText = `Portfolio: ${portfolioName}`;
  } else if (portfolioName && finalHeaderRightText) {
    finalHeaderRightText = `Portfolio: ${portfolioName} | ${finalHeaderRightText}`;
  }

  // Add header right text if available
  if (finalHeaderRightText) {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    // Calculate text width and position it on the right
    const textWidth = pdf.getTextWidth(finalHeaderRightText);
    const rightX = pageWidth - margin - textWidth;
    pdf.text(finalHeaderRightText, rightX, yPosition + 5);
  }

  yPosition += 10;

  // Get theme-aware background color
  const computedStyle = getComputedStyle(document.documentElement);
  const backgroundColor =
    computedStyle.getPropertyValue('--bq-surface-card').trim() ||
    computedStyle.getPropertyValue('--bq-surface-body').trim() ||
    '#ffffff';

  // Capture chart as image
  const canvas = await html2canvas(cardBody, {
    backgroundColor: backgroundColor,
    scale: 2,
    logging: false,
    useCORS: true,
    allowTaint: true,
    windowWidth: cardBody.scrollWidth,
    windowHeight: cardBody.scrollHeight,
  });

  const imgData = canvas.toDataURL('image/png');

  // Calculate available space for image
  const footerHeight = 6;
  const availableHeight = pageHeight - yPosition - margin - footerHeight;
  // Use minimal margins for full width
  const minMargin = 2;
  const availableWidth = pageWidth - 2 * minMargin;

  // Calculate image dimensions to fit available space while maintaining aspect ratio
  const aspectRatio = canvas.width / canvas.height;

  // Calculate dimensions for both width-constrained and height-constrained scenarios
  const widthConstrainedHeight = availableWidth / aspectRatio;
  const heightConstrainedWidth = availableHeight * aspectRatio;

  let imgWidth: number;
  let imgHeight: number;

  // Choose the scenario that fills the most space while fitting
  if (widthConstrainedHeight <= availableHeight) {
    // Width-constrained: use full width, calculated height
    imgWidth = availableWidth;
    imgHeight = widthConstrainedHeight;
  } else {
    // Height-constrained: use full height, calculated width
    imgHeight = availableHeight;
    imgWidth = heightConstrainedWidth;
  }

  // Center the image horizontally
  const xPosition = (pageWidth - imgWidth) / 2;

  // Center the image vertically within available space
  const yOffset = (availableHeight - imgHeight) / 2;

  // Add chart image to PDF
  pdf.addImage(
    imgData,
    'PNG',
    xPosition,
    yPosition + yOffset,
    imgWidth,
    imgHeight,
  );

  // Add footer with date
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'italic');
  pdf.text(`Generated on ${currentDate}`, margin, pageHeight - 5);

  // Save PDF
  const fileName = `${title.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
  pdf.save(fileName);
};
